export interface RoomDetail {
  id: string;
  colocId: string;
  name: string;
  floor: number;
  surface: number;
  bedSize: number | null;
  hasFurnitures: boolean;
  bathroomSharedWith: number;
  isTV: boolean;
  isCloset: boolean;
  isDesk: boolean;
  isLocker: boolean;
  agencyExpenses: number;
  depositAmount: number;
  priceNIC: number;
  charges: number;
  rentSupplement: number;
  availableStarting: string | null;
  slug: string;
  introTxt: string;
  lotNumber: string | null;
  roomNumber: number | null;
  tantiemes: number;
  dateCreated: string | null;
  // Enriched fields
  status: RoomStatus;
  tenantName: string | null;
  tenantId: string | null;
  noticeExpectedExitDate: string | null;
  enrollmentCandidatesCount: number;
  availabilityDate: string | null;
  leaseStartDate: string | null;
  monthlyRent: number | null;
  chargesProvision: number | null;
  /** Fermée par le bailleur : hors du site public, plus de candidature ni de
   *  réservation. Indépendant de l'occupation — un locataire en place y reste. */
  isClosed: boolean;
  /** Faux dès qu'un locataire (même parti), une candidature ou un bail l'ont
   *  touchée : elle se ferme au lieu de se supprimer. Calculé par le serveur. */
  supprimable: boolean;
}

/**
 * `reserved` : candidat accepté, bail non revenu signé. La chambre est promise
 * — elle ne doit plus recevoir de candidatures — mais personne n'y habite
 * encore. Avec une agence, cet état dure des semaines.
 */
export type RoomStatus = 'occupied' | 'reserved' | 'notice' | 'available';

export const getRoomStatusLabel = (status: RoomStatus): string => {
  switch (status) {
    case 'occupied': return 'Occupée';
    case 'reserved': return 'Réservée';
    case 'notice': return 'En préavis';
    case 'available': return 'Libre';
  }
};

// Teintes reprises du socle (`socle.css`) : mêmes significations, moins de
// saturation. Les quatre couleurs vives d'origine se disputaient l'attention
// avec les boutons de la ligne, au point que plus rien ne ressortait.
export const getRoomStatusColor = (status: RoomStatus): string => {
  switch (status) {
    case 'occupied': return '#1e8e4e';
    // Bleu : ni vert (personne n'habite), ni rouge (elle n'est plus à prendre).
    case 'reserved': return '#2b6cb0';
    case 'notice': return '#b26b00';
    // Rouge assumé : une chambre libre est un loyer qui ne rentre pas.
    case 'available': return '#c0392b';
  }
};

/** Badge d'une chambre fermée, à côté de son statut d'occupation. */
export const BADGE_FERMEE = { libelle: 'Fermée', couleur: '#5b6b7a' };

export interface RoomCreateRequest {
  name: string;
  floor?: number;
  surface?: number;
  bedSize?: number | null;
  hasFurnitures?: boolean;
  bathroomSharedWith: number;
  isTV?: boolean;
  isCloset?: boolean;
  isDesk?: boolean;
  isLocker?: boolean;
  agencyExpenses: number;
  depositAmount: number;
  priceNIC?: number;
  charges: number;
  rentSupplement?: number;
  availableStarting?: string | null;
  introTxt: string;
  lotNumber?: string | null;
  roomNumber?: number | null;
  tantiemes: number;
}

export type RoomUpdateRequest = Partial<RoomCreateRequest>;
